import { jsPDF } from 'jspdf';
import { saveAs } from 'file-saver';
import { Document, Packer, Paragraph, HeadingLevel, TextRun, AlignmentType } from 'docx';
import { generateMoodleXML } from './xmlGenerator';
import { typeShortCode } from '../mock';

const stripHtml = (s = '') => String(s).replace(/<[^>]+>/g, '').replace(/&nbsp;/g, ' ');

export const downloadXML = (quizTitle, questions) => {
  const xml = generateMoodleXML(quizTitle, questions);
  const blob = new Blob([xml], { type: 'application/xml;charset=utf-8' });
  const filename = (quizTitle || 'moodle-quiz').replace(/[^a-z0-9-_]+/gi, '_') + '.xml';
  saveAs(blob, filename);
};

const optLabel = (i) => String.fromCharCode(65 + i);

const questionToLines = (q, idx) => {
  const lines = [];
  lines.push(`${idx + 1}. [${typeShortCode(q.type)}] ${q.title || ''}`);
  if (q.question) lines.push(stripHtml(q.question));
  switch (q.type) {
    case 'mc-single':
    case 'mc-multiple': {
      (q.options || []).forEach((o, i) => {
        const correctSet = q.type === 'mc-single' ? [q.correct] : (q.corrects || []);
        const mark = correctSet.includes(i) ? '*' : ' ';
        lines.push(`   ${mark} ${optLabel(i)}. ${o || ''}`);
      });
      break;
    }
    case 'truefalse': {
      lines.push(`   Jawaban: ${q.correct === 'true' || q.correct === true ? 'Benar' : 'Salah'}`);
      break;
    }
    case 'short': {
      (q.answers || []).forEach((a, i) => lines.push(`   Jawaban ${i+1}: ${a}`));
      break;
    }
    case 'numerical': {
      lines.push(`   Jawaban: ${q.answer} (± ${q.tolerance || 0})`);
      break;
    }
    case 'matching': {
      (q.pairs || []).forEach((p, i) => lines.push(`   ${i+1}) ${p.q}  →  ${p.a}`));
      break;
    }
    case 'essay': {
      if (q.sampleAnswer) lines.push(`   Contoh: ${stripHtml(q.sampleAnswer)}`);
      break;
    }
    default: break;
  }
  lines.push('');
  return lines;
};

export const exportToPDF = (quizTitle, questions) => {
  const doc = new jsPDF({ unit: 'pt', format: 'a4' });
  const margin = 40;
  const pageWidth = doc.internal.pageSize.getWidth();
  const maxWidth = pageWidth - margin * 2;
  let y = margin;

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(18);
  doc.text(quizTitle || 'Moodle Quiz', margin, y);
  y += 24;
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(11);

  questions.forEach((q, idx) => {
    const lines = questionToLines(q, idx);
    for (const line of lines) {
      const wrapped = doc.splitTextToSize(line, maxWidth);
      for (const w of wrapped) {
        if (y > doc.internal.pageSize.getHeight() - margin) {
          doc.addPage();
          y = margin;
        }
        doc.text(w, margin, y);
        y += 16;
      }
    }
  });

  const filename = (quizTitle || 'moodle-quiz').replace(/[^a-z0-9-_]+/gi, '_') + '.pdf';
  doc.save(filename);
};

export const exportToDOCX = async (quizTitle, questions) => {
  const children = [];
  children.push(new Paragraph({
    text: quizTitle || 'Moodle Quiz',
    heading: HeadingLevel.HEADING_1,
    alignment: AlignmentType.LEFT,
  }));

  questions.forEach((q, idx) => {
    children.push(new Paragraph({
      children: [ new TextRun({ text: `${idx + 1}. [${typeShortCode(q.type)}] ${q.title || ''}`, bold: true }) ],
      spacing: { before: 200 },
    }));
    if (q.question) {
      children.push(new Paragraph({ text: stripHtml(q.question) }));
    }
    if (q.type === 'mc-single' || q.type === 'mc-multiple') {
      const correctSet = q.type === 'mc-single' ? [q.correct] : (q.corrects || []);
      (q.options || []).forEach((o, i) => {
        const mark = correctSet.includes(i) ? '✓ ' : '• ';
        children.push(new Paragraph({
          children: [ new TextRun({ text: `${mark}${optLabel(i)}. ${o || ''}`, bold: correctSet.includes(i) }) ],
          indent: { left: 400 },
        }));
      });
    } else if (q.type === 'truefalse') {
      children.push(new Paragraph({ text: `Jawaban: ${q.correct === 'true' ? 'Benar' : 'Salah'}`, indent: { left: 400 } }));
    } else if (q.type === 'short') {
      (q.answers || []).forEach((a, i) => children.push(new Paragraph({ text: `Jawaban ${i+1}: ${a}`, indent: { left: 400 } })));
    } else if (q.type === 'numerical') {
      children.push(new Paragraph({ text: `Jawaban: ${q.answer} (± ${q.tolerance || 0})`, indent: { left: 400 } }));
    } else if (q.type === 'matching') {
      (q.pairs || []).forEach((p, i) => children.push(new Paragraph({ text: `${i+1}) ${p.q}  →  ${p.a}`, indent: { left: 400 } })));
    } else if (q.type === 'essay') {
      if (q.sampleAnswer) children.push(new Paragraph({ text: `Contoh jawaban: ${stripHtml(q.sampleAnswer)}`, indent: { left: 400 } }));
    }
  });

  const doc = new Document({ sections: [{ properties: {}, children }] });
  const blob = await Packer.toBlob(doc);
  const filename = (quizTitle || 'moodle-quiz').replace(/[^a-z0-9-_]+/gi, '_') + '.docx';
  saveAs(blob, filename);
};
